import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MapPin, Upload, X, Loader2, Plus, Copy, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Location {
  id: string;
  name: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  phone: string | null;
  clinic_logo_url: string | null;
  provider_headshot_url: string | null;
  provider_bio: string | null;
  provider_name: string | null;
  provider_title: string | null;
  contact_name: string | null;
  contact_email: string | null;
  payment_provider: string | null;
  stripe_account_id: string | null;
  paypal_account_id: string | null;
  square_location_id: string | null;
  ghl_sub_account_id: string | null;
  booking_link: string;
  orderly_link: string | null;
  nuda_link: string | null;
}

export const LocationManagement = () => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string | null>(null);
  const [clinicName, setClinicName] = useState("");
  const [providerBio, setProviderBio] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerTitle, setProviderTitle] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [paymentProvider, setPaymentProvider] = useState("");
  const [stripeAccountId, setStripeAccountId] = useState("");
  const [paypalAccountId, setPaypalAccountId] = useState("");
  const [squareLocationId, setSquareLocationId] = useState("");
  const [ghlSubAccountId, setGhlSubAccountId] = useState("");
  const [emrBookingLink, setEmrBookingLink] = useState("");
  const [orderlyLink, setOrderlyLink] = useState("");
  const [nudaLink, setNudaLink] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  
  // Edit location address fields
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editPhone, setEditPhone] = useState("");
  
  // New location form state
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationAddress, setNewLocationAddress] = useState("");
  const [newLocationCity, setNewLocationCity] = useState("");
  const [newLocationState, setNewLocationState] = useState("");
  const [newLocationPhone, setNewLocationPhone] = useState("");
  const [newLocationBookingLink, setNewLocationBookingLink] = useState("");
  const [newLocationLatitude, setNewLocationLatitude] = useState("");
  const [newLocationLongitude, setNewLocationLongitude] = useState("");
  
  const queryClient = useQueryClient();

  const { data: locations, isLoading } = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as Location[];
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: async ({ 
      locationId, 
      logoUrl, 
      headshotUrl, 
      bio,
      clinicName,
      name,
      title,
      contactName,
      contactEmail,
      paymentProvider,
      stripeAccountId,
      paypalAccountId,
      squareLocationId,
      ghlSubAccountId,
      bookingLink,
      orderlyLink,
      nudaLink,
      address,
      city,
      state,
      phone
    }: { 
      locationId: string; 
      logoUrl?: string; 
      headshotUrl?: string; 
      bio?: string;
      clinicName?: string;
      name?: string;
      title?: string;
      contactName?: string;
      contactEmail?: string;
      paymentProvider?: string;
      stripeAccountId?: string;
      paypalAccountId?: string;
      squareLocationId?: string;
      ghlSubAccountId?: string;
      bookingLink?: string;
      orderlyLink?: string;
      nudaLink?: string;
      address?: string;
      city?: string;
      state?: string;
      phone?: string;
    }) => {
      const updates: any = {};
      if (logoUrl !== undefined) updates.clinic_logo_url = logoUrl;
      if (headshotUrl !== undefined) updates.provider_headshot_url = headshotUrl;
      if (bio !== undefined) updates.provider_bio = bio;
      if (clinicName !== undefined) updates.name = clinicName;
      if (name !== undefined) updates.provider_name = name;
      if (title !== undefined) updates.provider_title = title;
      if (contactName !== undefined) updates.contact_name = contactName;
      if (contactEmail !== undefined) updates.contact_email = contactEmail;
      if (paymentProvider !== undefined) updates.payment_provider = paymentProvider;
      if (stripeAccountId !== undefined) updates.stripe_account_id = stripeAccountId;
      if (paypalAccountId !== undefined) updates.paypal_account_id = paypalAccountId;
      if (squareLocationId !== undefined) updates.square_location_id = squareLocationId;
      if (ghlSubAccountId !== undefined) updates.ghl_sub_account_id = ghlSubAccountId;
      if (bookingLink !== undefined) updates.booking_link = bookingLink;
      if (orderlyLink !== undefined) updates.orderly_link = orderlyLink;
      if (nudaLink !== undefined) updates.nuda_link = nudaLink;
      if (address !== undefined) updates.address = address;
      if (city !== undefined) updates.city = city;
      if (state !== undefined) updates.state = state;
      if (phone !== undefined) updates.phone = phone;

      const { error } = await supabase
        .from('locations')
        .update(updates)
        .eq('id', locationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success("Location updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update location: ${error.message}`);
    },
  });

  const deleteLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success("Location deleted successfully");
      setIsDeleteDialogOpen(false);
      setLocationToDelete(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete location: ${error.message}`);
    },
  });

  const createLocationMutation = useMutation({
    mutationFn: async (newLocation: {
      name: string;
      slug: string;
      address: string;
      city: string;
      state: string;
      phone: string;
      booking_link: string;
      latitude: number;
      longitude: number;
    }) => {
      const { error } = await supabase
        .from('locations')
        .insert(newLocation);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success("Location created successfully");
      resetAddForm();
      setIsAddDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create location: ${error.message}`);
    },
  });

  const duplicateLocationMutation = useMutation({
    mutationFn: async (location: Location) => {
      // Fetch full location data
      const { data: fullLocation, error: fetchError } = await supabase
        .from('locations')
        .select('*')
        .eq('id', location.id)
        .single();

      if (fetchError) throw fetchError;

      // Create new slug with copy suffix
      const timestamp = Date.now();
      const newSlug = `${fullLocation.slug}-copy-${timestamp}`;
      const newName = `${fullLocation.name} (Copy)`;

      // Remove id and create new record
      const { id, created_at, updated_at, ...locationData } = fullLocation;
      
      const { error } = await supabase
        .from('locations')
        .insert({
          ...locationData,
          name: newName,
          slug: newSlug,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast.success("Location duplicated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to duplicate location: ${error.message}`);
    },
  });

  const resetAddForm = () => {
    setNewLocationName("");
    setNewLocationAddress("");
    setNewLocationCity("");
    setNewLocationState("");
    setNewLocationPhone("");
    setNewLocationBookingLink("");
    setNewLocationLatitude("");
    setNewLocationLongitude("");
  };

  const handleAddLocation = async () => {
    if (!newLocationName || !newLocationAddress || !newLocationCity || !newLocationState || !newLocationBookingLink) {
      toast.error("Please fill in all required fields");
      return;
    }

    const slug = newLocationName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    
    await createLocationMutation.mutateAsync({
      name: newLocationName,
      slug,
      address: newLocationAddress,
      city: newLocationCity,
      state: newLocationState,
      phone: newLocationPhone,
      booking_link: newLocationBookingLink,
      latitude: parseFloat(newLocationLatitude) || 0,
      longitude: parseFloat(newLocationLongitude) || 0,
    });
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeadshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      setHeadshotFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeadshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File, bucket: string, path: string): Promise<string> => {
    // Delete old file if it exists
    const { error: deleteError } = await supabase.storage
      .from(bucket)
      .remove([path]);
    
    if (deleteError) {
      console.log("No existing file to delete or deletion failed:", deleteError);
    }

    // Upload new file
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return data.publicUrl;
  };

  const handleSave = async () => {
    if (!selectedLocation) return;

    setIsUploading(true);
    try {
      let logoUrl = selectedLocation.clinic_logo_url;
      let headshotUrl = selectedLocation.provider_headshot_url;

      // Upload logo if changed
      if (logoFile) {
        const logoPath = `${selectedLocation.slug}-logo.${logoFile.name.split('.').pop()}`;
        logoUrl = await uploadImage(logoFile, 'clinic-logos', logoPath);
      }

      // Upload headshot if changed
      if (headshotFile) {
        const headshotPath = `${selectedLocation.slug}-headshot.${headshotFile.name.split('.').pop()}`;
        headshotUrl = await uploadImage(headshotFile, 'provider-headshots', headshotPath);
      }

      // Update location
      await updateLocationMutation.mutateAsync({
        locationId: selectedLocation.id,
        logoUrl,
        headshotUrl,
        bio: providerBio,
        clinicName,
        name: providerName,
        title: providerTitle,
        contactName,
        contactEmail,
        paymentProvider,
        stripeAccountId,
        paypalAccountId,
        squareLocationId,
        ghlSubAccountId,
        bookingLink: emrBookingLink,
        orderlyLink,
        nudaLink,
        address: editAddress,
        city: editCity,
        state: editState,
        phone: editPhone,
      });

      // Reset form
      setIsDialogOpen(false);
      setSelectedLocation(null);
      setLogoFile(null);
      setHeadshotFile(null);
      setLogoPreview(null);
      setHeadshotPreview(null);
      setClinicName("");
      setProviderBio("");
      setProviderName("");
      setProviderTitle("");
      setContactName("");
      setContactEmail("");
      setPaymentProvider("");
      setStripeAccountId("");
      setPaypalAccountId("");
      setSquareLocationId("");
        setGhlSubAccountId("");
        setEmrBookingLink("");
        setOrderlyLink("");
        setNudaLink("");
        setEditAddress("");
        setEditCity("");
        setEditState("");
        setEditPhone("");
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const openEditDialog = (location: Location) => {
    setSelectedLocation(location);
    setClinicName(location.name || "");
    setProviderBio(location.provider_bio || "");
    setProviderName(location.provider_name || "");
    setProviderTitle(location.provider_title || "");
    setContactName(location.contact_name || "");
    setContactEmail(location.contact_email || "");
    setPaymentProvider(location.payment_provider || "");
    setStripeAccountId(location.stripe_account_id || "");
    setPaypalAccountId(location.paypal_account_id || "");
    setSquareLocationId(location.square_location_id || "");
    setGhlSubAccountId(location.ghl_sub_account_id || "");
    setEmrBookingLink(location.booking_link || "");
    setOrderlyLink(location.orderly_link || "");
    setNudaLink(location.nuda_link || "");
    setEditAddress(location.address || "");
    setEditCity(location.city || "");
    setEditState(location.state || "");
    setEditPhone(location.phone || "");
    setLogoPreview(location.clinic_logo_url);
    setHeadshotPreview(location.provider_headshot_url);
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (location: Location) => {
    setLocationToDelete(location);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Location Management</h2>
            <p className="text-muted-foreground mt-1">Manage clinic logos, provider headshots, and bios</p>
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Location</TableHead>
              <TableHead>City, State</TableHead>
              <TableHead>Logo</TableHead>
              <TableHead>Headshot</TableHead>
              <TableHead>Bio</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations?.map((location) => (
              <TableRow key={location.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {location.name}
                  </div>
                </TableCell>
                <TableCell>{location.city}, {location.state}</TableCell>
                <TableCell>
                  {location.clinic_logo_url ? (
                    <img src={location.clinic_logo_url} alt="Logo" className="h-8 w-8 object-contain" />
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo</span>
                  )}
                </TableCell>
                <TableCell>
                  {location.provider_headshot_url ? (
                    <img src={location.provider_headshot_url} alt="Headshot" className="h-8 w-8 object-cover rounded-full" />
                  ) : (
                    <span className="text-xs text-muted-foreground">No photo</span>
                  )}
                </TableCell>
                <TableCell>
                  {location.provider_bio ? (
                    <span className="text-xs text-muted-foreground">
                      {location.provider_bio.substring(0, 50)}...
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">No bio</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => openEditDialog(location)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => duplicateLocationMutation.mutate(location)}
                      disabled={duplicateLocationMutation.isPending}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openDeleteDialog(location)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {selectedLocation?.name}</DialogTitle>
            <DialogDescription>
              Upload clinic logo, provider headshot, and edit provider bio
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Clinic Name */}
            <div className="space-y-2">
              <Label htmlFor="clinic-name">Clinic Name</Label>
              <Input
                id="clinic-name"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                placeholder="e.g., Restorative Injectables"
              />
              <p className="text-xs text-muted-foreground">
                The name of the clinic/location.
              </p>
            </div>

            {/* Address Fields */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="font-semibold text-lg">Location Address</h3>
              
              <div className="space-y-2">
                <Label htmlFor="edit-address">Street Address</Label>
                <Input
                  id="edit-address"
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  placeholder="123 Main Street, Suite 100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-city">City</Label>
                  <Input
                    id="edit-city"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    placeholder="Dallas"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-state">State</Label>
                  <Input
                    id="edit-state"
                    value={editState}
                    onChange={(e) => setEditState(e.target.value)}
                    placeholder="TX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Clinic Logo */}
            <div className="space-y-2">
              <Label htmlFor="logo">Clinic Logo</Label>
              <div className="flex items-start gap-4">
                {logoPreview && (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="h-24 w-24 object-contain border border-border rounded-lg p-2"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleLogoChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, or WEBP. Max 2MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Provider Headshot */}
            <div className="space-y-2">
              <Label htmlFor="headshot">Provider Headshot</Label>
              <div className="flex items-start gap-4">
                {headshotPreview && (
                  <div className="relative">
                    <img 
                      src={headshotPreview} 
                      alt="Headshot preview" 
                      className="h-24 w-24 object-cover rounded-full border-2 border-primary"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={() => {
                        setHeadshotFile(null);
                        setHeadshotPreview(null);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    id="headshot"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleHeadshotChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, or WEBP. Max 2MB. Square photos work best.
                  </p>
                </div>
              </div>
            </div>

            {/* Provider Name */}
            <div className="space-y-2">
              <Label htmlFor="provider-name">Provider Name</Label>
              <Input
                id="provider-name"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                placeholder="e.g., Taylor Campbell, NP"
              />
              <p className="text-xs text-muted-foreground">
                The provider&apos;s full name and credentials.
              </p>
            </div>

            {/* Provider Title */}
            <div className="space-y-2">
              <Label htmlFor="provider-title">Provider Title</Label>
              <Input
                id="provider-title"
                value={providerTitle}
                onChange={(e) => setProviderTitle(e.target.value)}
                placeholder="e.g., Aesthetic Injector & Founder of Restorative Injectables"
              />
              <p className="text-xs text-muted-foreground">
                The provider&apos;s professional title and role.
              </p>
            </div>

            {/* Provider Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio">Provider Bio</Label>
              <Textarea
                id="bio"
                value={providerBio}
                onChange={(e) => setProviderBio(e.target.value)}
                placeholder="Enter provider bio, credentials, and experience..."
                className="min-h-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                This will be displayed on the provider profile page.
              </p>
            </div>

            {/* Contact Information Section */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="font-semibold text-lg">Contact Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Primary contact person name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@clinic.com"
                />
              </div>
            </div>

            {/* Payment Settings Section */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="font-semibold text-lg">Payment Settings</h3>
              <p className="text-sm text-muted-foreground">
                Configure payment provider to collect $25 consultation fees
              </p>

              <div className="space-y-2">
                <Label htmlFor="paymentProvider">Payment Provider</Label>
                <select
                  id="paymentProvider"
                  value={paymentProvider}
                  onChange={(e) => setPaymentProvider(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select provider...</option>
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="square">Square</option>
                </select>
              </div>

              {paymentProvider === 'stripe' && (
                <div className="space-y-2">
                  <Label htmlFor="stripeAccountId">Stripe Account ID</Label>
                  <Input
                    id="stripeAccountId"
                    value={stripeAccountId}
                    onChange={(e) => setStripeAccountId(e.target.value)}
                    placeholder="acct_..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in your Stripe Dashboard under Settings → Account details
                  </p>
                </div>
              )}

              {paymentProvider === 'paypal' && (
                <div className="space-y-2">
                  <Label htmlFor="paypalAccountId">PayPal Account ID / Email</Label>
                  <Input
                    id="paypalAccountId"
                    value={paypalAccountId}
                    onChange={(e) => setPaypalAccountId(e.target.value)}
                    placeholder="your-paypal-email@example.com"
                  />
                </div>
              )}

              {paymentProvider === 'square' && (
                <div className="space-y-2">
                  <Label htmlFor="squareLocationId">Square Location ID</Label>
                  <Input
                    id="squareLocationId"
                    value={squareLocationId}
                    onChange={(e) => setSquareLocationId(e.target.value)}
                    placeholder="Location ID from Square Dashboard"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in Square Dashboard → Locations
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* EMR Booking Link Section */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="font-semibold text-lg">EMR Booking Link</h3>
            <div className="space-y-2">
              <Label htmlFor="emrBookingLink">EMR/Booking System URL</Label>
              <Input
                id="emrBookingLink"
                value={emrBookingLink}
                onChange={(e) => setEmrBookingLink(e.target.value)}
                placeholder="https://booking.example.com/clinic"
              />
              <p className="text-xs text-muted-foreground">
                The direct link to this location's EMR or booking system. This will be used for patient appointment scheduling.
              </p>
            </div>
          </div>

          {/* Partner Links Section */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="font-semibold text-lg">Partner Links</h3>
            <p className="text-sm text-muted-foreground">
              Configure external partner links for lead routing. GLP-1 weight loss leads go to Orderly, peptide leads go to Nuda Labs.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="orderlyLink">Orderly Link (GLP-1 Weight Loss)</Label>
              <Input
                id="orderlyLink"
                value={orderlyLink}
                onChange={(e) => setOrderlyLink(e.target.value)}
                placeholder="https://patients.orderlymeds.com/eligibility?orid=..."
              />
              <p className="text-xs text-muted-foreground">
                Weight loss / GLP-1 leads will be directed to this Orderly partner link.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nudaLink">Nuda Labs Link (Peptides)</Label>
              <Input
                id="nudaLink"
                value={nudaLink}
                onChange={(e) => setNudaLink(e.target.value)}
                placeholder="https://nudalabs.com/partner/..."
              />
              <p className="text-xs text-muted-foreground">
                Peptide therapy leads will be directed to this Nuda Labs partner link.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">GoHighLevel Integration</h3>
            <div className="space-y-2">
              <Label htmlFor="ghlSubAccountId">GHL Sub-Account ID</Label>
              <Input
                id="ghlSubAccountId"
                value={ghlSubAccountId}
                onChange={(e) => setGhlSubAccountId(e.target.value)}
                placeholder="Enter GoHighLevel Sub-Account ID"
              />
              <p className="text-xs text-muted-foreground">
                Find this in your GoHighLevel dashboard under Settings → Company Info. This ID is used to sync contacts from bookings to your GHL account.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setSelectedLocation(null);
                setLogoFile(null);
                setHeadshotFile(null);
                setLogoPreview(null);
                setHeadshotPreview(null);
                setClinicName("");
                setProviderBio("");
                setProviderName("");
                setProviderTitle("");
                setContactName("");
                setContactEmail("");
                setPaymentProvider("");
                setStripeAccountId("");
                setPaypalAccountId("");
                setSquareLocationId("");
                setGhlSubAccountId("");
                setEmrBookingLink("");
                setOrderlyLink("");
                setNudaLink("");
                setEditAddress("");
                setEditCity("");
                setEditState("");
                setEditPhone("");
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Location Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Location</DialogTitle>
            <DialogDescription>
              Enter the details for the new clinic location
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-name">Clinic Name *</Label>
              <Input
                id="new-name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="e.g., Restorative Injectables"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-address">Address *</Label>
              <Input
                id="new-address"
                value={newLocationAddress}
                onChange={(e) => setNewLocationAddress(e.target.value)}
                placeholder="123 Main Street, Suite 100"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-city">City *</Label>
                <Input
                  id="new-city"
                  value={newLocationCity}
                  onChange={(e) => setNewLocationCity(e.target.value)}
                  placeholder="Dallas"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-state">State *</Label>
                <Input
                  id="new-state"
                  value={newLocationState}
                  onChange={(e) => setNewLocationState(e.target.value)}
                  placeholder="TX"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-phone">Phone</Label>
              <Input
                id="new-phone"
                value={newLocationPhone}
                onChange={(e) => setNewLocationPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-booking">Booking Link *</Label>
              <Input
                id="new-booking"
                value={newLocationBookingLink}
                onChange={(e) => setNewLocationBookingLink(e.target.value)}
                placeholder="https://booking.example.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-lat">Latitude</Label>
                <Input
                  id="new-lat"
                  type="number"
                  step="any"
                  value={newLocationLatitude}
                  onChange={(e) => setNewLocationLatitude(e.target.value)}
                  placeholder="32.7767"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-lng">Longitude</Label>
                <Input
                  id="new-lng"
                  type="number"
                  step="any"
                  value={newLocationLongitude}
                  onChange={(e) => setNewLocationLongitude(e.target.value)}
                  placeholder="-96.7970"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddLocation}
              disabled={createLocationMutation.isPending}
            >
              {createLocationMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Location
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{locationToDelete?.name}</strong>? This action cannot be undone. All associated data including bookings, memberships, and check-ins may be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setLocationToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => locationToDelete && deleteLocationMutation.mutate(locationToDelete.id)}
              disabled={deleteLocationMutation.isPending}
            >
              {deleteLocationMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Location"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
